/**
 * editorLspExtensions.ts — CodeMirror 6 extensions for LSP-like features.
 * Provides go-to-definition (Ctrl+Click), hover tooltips, and linting
 * using the isomorphic symbol index + diagnostics engine.
 */

import { EditorView, Tooltip, hoverTooltip } from '@codemirror/view';
import { Extension } from '@codemirror/state';
import { linter, type Diagnostic as CmDiagnostic } from '@codemirror/lint';
import {
  type SymbolIndex,
  findDefinition,
  findSymbolAtPosition,
} from './symbolIndex';
import { runFileDiagnostics, type Diagnostic } from './diagnosticsEngine';
import type { FileInput } from './symbolIndex';

// ── Types ────────────────────────────────────────────────────────────

export interface LspConfig {
  /** Current file info */
  fileName: string;
  filePath: string;
  /** Getter for the latest symbol index (may update as files change) */
  getIndex: () => SymbolIndex | null;
  /** Getter for the current file input (for diagnostics) */
  getFileInput: () => FileInput | null;
  /** Navigate to a definition in another file */
  onNavigate?: (filePath: string, line: number, column: number) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Extract a word (identifier) at the given document position */
function wordAt(view: EditorView, pos: number): { word: string; from: number; to: number } | null {
  const line = view.state.doc.lineAt(pos);
  const text = line.text;
  const col = pos - line.from;

  // Walk left to find word start
  let start = col;
  while (start > 0 && /[\w$]/.test(text[start - 1])) start--;

  // Walk right to find word end
  let end = col;
  while (end < text.length && /[\w$]/.test(text[end])) end++;

  if (start === end) return null;
  const word = text.slice(start, end);
  if (!/^[A-Za-z_$]/.test(word)) return null;

  return { word, from: line.from + start, to: line.from + end };
}

// ── Go-to-Definition (Ctrl+Click) ───────────────────────────────────

function createGoToDefinitionExtension(config: LspConfig): Extension {
  return EditorView.domEventHandlers({
    click(event: MouseEvent, view: EditorView) {
      if (!(event.ctrlKey || event.metaKey)) return false;

      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos == null) return false;

      const w = wordAt(view, pos);
      if (!w) return false;

      const index = config.getIndex();
      if (!index) return false;

      const line = view.state.doc.lineAt(pos);
      const lineNumber = line.number;
      const column = pos - line.from;

      // Try to find definition
      const def = findDefinition(index, w.word);
      if (!def) return false;

      // If definition is in the same file, jump to it
      if (def.filePath === config.filePath) {
        const targetLine = view.state.doc.line(Math.min(def.line, view.state.doc.lines));
        view.dispatch({
          selection: { anchor: targetLine.from + Math.min(def.column, targetLine.length) },
          scrollIntoView: true,
        });
        event.preventDefault();
        return true;
      }

      // Navigate to another file
      if (config.onNavigate) {
        config.onNavigate(def.filePath, def.line, def.column);
        event.preventDefault();
        return true;
      }

      return false;
    },
  });
}

// ── Ctrl+Click cursor style ─────────────────────────────────────────

function createCtrlClickCursorExtension(config: LspConfig): Extension {
  return EditorView.domEventHandlers({
    mousemove(event: MouseEvent, view: EditorView) {
      const editorDom = view.dom;
      if (event.ctrlKey || event.metaKey) {
        const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
        if (pos != null) {
          const w = wordAt(view, pos);
          if (w) {
            editorDom.style.cursor = 'pointer';
            return false;
          }
        }
      }
      editorDom.style.cursor = '';
      return false;
    },
    mouseleave(_event: MouseEvent, view: EditorView) {
      view.dom.style.cursor = '';
      return false;
    },
  });
}

// ── Hover Tooltip ───────────────────────────────────────────────────

function createHoverExtension(config: LspConfig): Extension {
  return hoverTooltip((view, pos, side): Tooltip | null => {
    const w = wordAt(view, pos);
    if (!w) return null;

    const index = config.getIndex();
    if (!index) return null;

    const line = view.state.doc.lineAt(pos);
    const lineNumber = line.number;
    const column = pos - line.from;

    const code = view.state.doc.toString();
    const symbol = findSymbolAtPosition(index, config.filePath, lineNumber, column, code);
    // Also try looking up definition across files
    const def = findDefinition(index, w.word);

    if (!symbol && !def) return null;

    return {
      pos: w.from,
      end: w.to,
      above: true,
      create() {
        const dom = document.createElement('div');
        dom.className = 'cm-lsp-tooltip';
        dom.style.cssText =
          'padding:6px 10px;font-size:12px;font-family:var(--font-mono,"JetBrains Mono",monospace);' +
          'max-width:400px;line-height:1.4;';

        const entry = symbol || def;
        if (!entry) return { dom };

        const kind = document.createElement('span');
        kind.style.cssText = 'color:#7cacf8;font-weight:600;';
        kind.textContent = entry.kind;
        dom.appendChild(kind);

        const name = document.createElement('span');
        name.style.cssText = 'color:#e2e8f0;margin-left:6px;font-weight:500;';
        name.textContent = entry.name;
        dom.appendChild(name);

        if (entry.exported) {
          const exp = document.createElement('span');
          exp.style.cssText = 'color:#68d391;margin-left:6px;font-size:11px;';
          exp.textContent = 'exported';
          dom.appendChild(exp);
        }

        if (def && def.filePath !== config.filePath) {
          const loc = document.createElement('div');
          loc.style.cssText = 'color:#a0aec0;font-size:11px;margin-top:4px;';
          loc.textContent = `${def.fileName}:${def.line}`;
          dom.appendChild(loc);

          const hint = document.createElement('div');
          hint.style.cssText = 'color:#718096;font-size:10px;margin-top:2px;';
          hint.textContent = 'Ctrl+Click to go to definition';
          dom.appendChild(hint);
        }

        return { dom };
      },
    };
  });
}

// ── Linting ─────────────────────────────────────────────────────────

function createLintingExtension(config: LspConfig): Extension {
  return linter(
    (view) => {
      const index = config.getIndex();
      const fileInput = config.getFileInput();
      if (!index || !fileInput) return [];

      // Run diagnostics
      const diagnostics = runFileDiagnostics(fileInput, index);

      // Convert to CodeMirror diagnostics
      return diagnostics
        .map((d: Diagnostic): CmDiagnostic | null => {
          try {
            const line = view.state.doc.line(Math.min(d.line, view.state.doc.lines));
            const from = line.from + Math.min(d.column, line.length);
            const to = line.from + Math.min(d.endColumn, line.length);

            return {
              from: Math.max(0, from),
              to: Math.max(from, to),
              severity: d.severity === 'error' ? 'error' : d.severity === 'warning' ? 'warning' : 'info',
              message: d.message,
              source: 'tasking-lsp',
            };
          } catch {
            return null;
          }
        })
        .filter((d): d is CmDiagnostic => d !== null);
    },
    { delay: 1500 },
  );
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Create all LSP-like CodeMirror extensions.
 * Returns an array of extensions to spread into the editor's extension list.
 */
export function createLspExtensions(config: LspConfig): Extension[] {
  return [
    createGoToDefinitionExtension(config),
    createCtrlClickCursorExtension(config),
    createHoverExtension(config),
    createLintingExtension(config),
  ];
}
